import React, { useCallback } from "react";
import { SendFromInput } from "./inputs/SendFromInput";
import { SendOption } from "./ComposeMessage/SendOption";
import { useOutletContext } from "react-router-dom";
import { BulkMessageOutletContext } from "./SendBulkMessage";
import { MessageEditor } from "@/components/MessageEditor";
import { useSenderInfo } from "./hooks/useSenderInfo";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormValues, MessagePartInputExtended } from "./types";
import { filterEmptyMessageParts } from "./utils/mutationBuilders";

export const ComposeMessage = () => {
  const { isActive, timeZone } = useOutletContext<BulkMessageOutletContext>();

  const { name: senderName, profilePhoto: senderProfilePhoto } =
    useSenderInfo();
  const {
    formState: { errors },
    setValue,
    getValues,
    watch,
    register,
    clearErrors,
  } = useFormContext<FormValues>();
  const { t } = useTranslation("translation", {
    keyPrefix: "bulkMessaging.composeMessage",
  });

  const messageParts = watch("messageParts") || [];

  const riseActionId = getValues("filters.riseActionId");

  const handleMessagePartsChange = useCallback(
    (newMessageParts: MessagePartInputExtended[]) => {
      clearErrors("messageParts");
      setValue("messageParts", newMessageParts);
    },
    [setValue, clearErrors],
  );

  register("messageParts", {
    validate: {
      required: (parts) => {
        if (filterEmptyMessageParts(parts).length) {
          return true;
        }
        return t("inputs.messageParts.error");
      },
    },
  });

  return (
    <div className="grid grid-cols-2">
      <div className="flex flex-col gap-6 p-6">
        <SendFromInput />
        <SendOption
          timeZone={timeZone ?? undefined}
          loading={false}
          isActive={isActive}
          disableCampaign={!!riseActionId}
        />
      </div>
      <div className="flex flex-col items-center gap-4 bg-accent-light/50 px-6 pt-[14px] pb-9">
        <p className="text-sm font-bold text-foreground">{t("editMessage")}</p>
        <div className="flex flex-col justify-center gap-1">
          <MessageEditor
            messageParts={messageParts}
            onChange={handleMessagePartsChange}
            className="w-full max-w-[700px]"
            senderName={senderName}
            senderProfilePhoto={senderProfilePhoto}
          />
          {errors.messageParts && (
            <small className="text-xs font-medium text-red-700">
              {errors.messageParts.message}
            </small>
          )}
        </div>
      </div>
    </div>
  );
};
